import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PiSparkle } from 'react-icons/pi'
import { ActionSummaryCard } from '../../../components/ActionSummaryCard/ActionSummaryCard'
import { PerformanceDashboardCard } from '../../../components/PerformanceDashboard'
import {
  generateActionSummary,
  type ActionSummaryData,
} from '../../../services/action_summary_api'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/authentication/useAuth'
import apiService from '../../../services/api'
import ReactSelectWrapper from '../../../components/FormUtils/ReactSelectWrapper'
import type { SingleValue } from 'react-select'
import { useHideFooter } from '../../../context/layout'
import { NewFooter } from '../../../components/Layout/Footer/NewFooter'
import { ErrorBoundary } from '../../../components/UI/ErrorBoundary'

/**
 * Home page - Your daily action summary and priorities
 *
 * This is the main landing page where users start their day.
 * Shows AI-generated action summary with prioritized deals, emails, and campaigns.
 *
 * Superusers can select any user to view/generate their summary.
 */
export function HomePage() {
  useHideFooter()
  const [isGenerating, setIsGenerating] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>()
  const [latestSummaryMeta, setLatestSummaryMeta] = useState<{
    id: number | null
    generatedAt: string | null
  }>({ id: null, generatedAt: null })
  const [pendingSummaryMeta, setPendingSummaryMeta] = useState<{
    id: number | null
    generatedAt: string | null
  } | null>(null)
  const queryClient = useQueryClient()
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const { user, isLimited } = useAuth()
  const { data: users } = apiService().useGetUsers(
    !user?.is_superuser || isLimited
  )

  const isSuperuser = user?.is_superuser

  const summaryQueryKey = useMemo(
    () => ['action-summary', selectedUserId ?? 'self'],
    [selectedUserId]
  )

  const invalidateSummaryQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: summaryQueryKey })
  }, [queryClient, summaryQueryKey])

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  const startPolling = useCallback(() => {
    stopPolling()
    invalidateSummaryQuery()
    pollingRef.current = setInterval(() => {
      invalidateSummaryQuery()
    }, 5000)
  }, [invalidateSummaryQuery, stopPolling])

  useEffect(() => {
    return () => {
      stopPolling()
    }
  }, [stopPolling])

  useEffect(() => {
    stopPolling()
    setPendingSummaryMeta(null)
    setIsGenerating(false)
    setLatestSummaryMeta({ id: null, generatedAt: null })
  }, [selectedUserId, stopPolling])

  const handleGenerateSummary = async () => {
    setIsGenerating(true)
    setPendingSummaryMeta(latestSummaryMeta)
    try {
      const response = await generateActionSummary(selectedUserId)
      if (response?.task_id) {
        // New generation task was queued — poll until it completes
        startPolling()
      } else {
        // Summary already exists for today (HTTP 200) — no task queued
        setIsGenerating(false)
        setPendingSummaryMeta(null)
        invalidateSummaryQuery()
      }
    } catch (error) {
      console.error('Failed to generate summary:', error)
      setIsGenerating(false)
      setPendingSummaryMeta(null)
      stopPolling()
    }
  }

  const handleSummaryLoaded = useCallback(
    (summary: ActionSummaryData | null) => {
      setLatestSummaryMeta({
        id: summary?.id ?? null,
        generatedAt: summary?.created ?? null,
      })

      if (!summary || !pendingSummaryMeta) {
        return
      }

      if (summary.status === 'COMPLETED' || summary.status === 'FAILED') {
        // Stop polling when generation finishes (success or failure)
        setIsGenerating(false)
        setPendingSummaryMeta(null)
        stopPolling()
      }
    },
    [pendingSummaryMeta, stopPolling]
  )

  const userOptions = useMemo(
    () =>
      users?.map((u) => ({
        value: u.id,
        label: `${u.first_name} ${u.last_name} (${u.email})`,
      })) ?? [],
    [users]
  )
  const selectedUser = users?.find((u) => u.id === selectedUserId)
  const selectedUserOption = userOptions.find(
    (option) => option.value === selectedUserId
  )

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Good morning!</h1>
            <p className="mt-2 text-lg text-gray-600">
              Here&apos;s what needs your attention today
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* Admin user selector */}
            {isSuperuser && users && (
              <ReactSelectWrapper
                className="min-w-[240px]"
                options={userOptions}
                value={selectedUserOption ?? null}
                onChange={(option: unknown) => {
                  const typedOption = option as SingleValue<{
                    label: string
                    value: number
                  }>
                  setSelectedUserId(typedOption ? typedOption.value : undefined)
                }}
                placeholder="My Summary"
                isClearable
              />
            )}

            {/* Generate summary button */}
            <button
              onClick={handleGenerateSummary}
              disabled={isGenerating}
              className="flex items-center gap-2 px-4 py-2 bg-fb-dark-green text-white rounded-lg hover:bg-fb-dark-green-hover disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              title={
                selectedUserId
                  ? `Generate summary for ${selectedUser?.email}`
                  : 'Generate a new action summary'
              }
            >
              <PiSparkle size={20} />
              {isGenerating ? 'Generating...' : 'Generate Summary'}
            </button>
          </div>
        </div>

        {/* Personal Performance Dashboard */}
        <section className="mb-8">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            Your Performance
          </h2>
          <PerformanceDashboardCard userId={selectedUserId} />
        </section>

        {/* AI Action Summary */}
        <section>
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            AI Action Plan
          </h2>
          <ErrorBoundary
            fallback={
              <div className="rounded-lg border border-red-200 bg-red-50 p-6">
                <p className="text-red-800 font-medium">
                  Failed to load action summary
                </p>
                <p className="mt-1 text-sm text-red-600">
                  Please try refreshing the page
                </p>
              </div>
            }
          >
            <ActionSummaryCard
              userId={selectedUserId}
              onSummaryLoaded={handleSummaryLoaded}
            />
          </ErrorBoundary>
        </section>

        <div className="mt-10">
          <NewFooter className="rounded-none px-0" />
        </div>
      </div>
    </div>
  )
}
